import { useState, useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { Capacitor } from "@capacitor/core";
import { ENABLE_PROTECTED_ROUTE_TELEMETRY } from "../constants/logging";

interface UseSessionTimeoutProps {
  inactivityTimeout?: number;
  warningTimeout?: number;
  onLogout: () => void;
  enabled?: boolean;
}

export const useSessionTimeout = ({
  inactivityTimeout = 28 * 60 * 1000,
  warningTimeout = 2 * 60 * 1000,
  onLogout,
  enabled = true,
}: UseSessionTimeoutProps) => {
  const [showModal, setShowModal] = useState(false);
  const [remainingTime, setRemainingTime] = useState(warningTimeout);

  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const showModalRef = useRef(showModal);
  const onLogoutRef = useRef(onLogout);
  const inactivityTimeoutRef = useRef(inactivityTimeout);
  const warningTimeoutRef = useRef(warningTimeout);

  useEffect(() => {
    showModalRef.current = showModal;
  }, [showModal]);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    inactivityTimeoutRef.current = inactivityTimeout;
  }, [inactivityTimeout]);

  useEffect(() => {
    warningTimeoutRef.current = warningTimeout;
  }, [warningTimeout]);

  const clearAllTimers = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  };

  const showWarningModal = () => {
    setShowModal(true);
    setRemainingTime(warningTimeoutRef.current);

    countdownIntervalRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        const newTime = prev - 1000;
        return newTime <= 0 ? 0 : newTime;
      });
    }, 1000);

    warningTimerRef.current = setTimeout(() => {
      clearAllTimers();
      setShowModal(false);
      if (ENABLE_PROTECTED_ROUTE_TELEMETRY) {
        Sentry.withScope((scope) => {
          scope.setTag("module", "useSessionTimeout");
          scope.setTag("logout_trigger", "inactivity_timeout");
          scope.setTag("platform", Capacitor.getPlatform());
          scope.setTag("isNative", String(Capacitor.isNativePlatform()));
          scope.setExtra("inactivityTimeout", inactivityTimeoutRef.current);
          scope.setExtra("warningTimeout", warningTimeoutRef.current);
          scope.setExtra("currentTime", new Date().toISOString());
          Sentry.captureMessage("sessionTimeout.inactivity_logout", "warning");
        });
      }
      onLogoutRef.current();
    }, warningTimeoutRef.current);
  };

  const resetInactivityTimer = () => {
    if (!enabled) return;

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    if (!showModalRef.current) {
      inactivityTimerRef.current = setTimeout(() => {
        showWarningModal();
      }, inactivityTimeoutRef.current);
    }
  };

  const continueSession = () => {
    clearAllTimers();
    setShowModal(false);

    inactivityTimerRef.current = setTimeout(() => {
      showWarningModal();
    }, inactivityTimeoutRef.current);
  };

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      return;
    }

    const events = [
      "mousedown",
      "mousemove",
      "keydown",
      "scroll",
      "touchstart",
      "click",
    ];

    const handleActivity = () => {
      if (!showModalRef.current) {
        resetInactivityTimer();
      }
    };

    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    resetInactivityTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      clearAllTimers();
    };
  }, [enabled]);

  return {
    showModal,
    remainingTime: formatTime(remainingTime),
    continueSession,
  };
};
